import { useCallback, useRef, useState } from 'react';

import { useEvent, useFormField } from '@american-autonomy/ag-ui';

import type { FieldValues, Item, Props, UseMultiSelectFieldReturn } from './types';

const useMultiSelectField = <T extends FieldValues>({
    control,
    isOpen: controlledIsOpen,
    isRequired = false,
    name,
    onPanelClose,
    onPanelOpen,
    ref,
    requiredMessage
}: Props<T>): UseMultiSelectFieldReturn => {
    const [internalIsOpen, setInternalIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    const isPanelOpen = controlledIsOpen ?? internalIsOpen;

    const {
        field: { onChange, value }
    } = useFormField({
        control,
        name,
        rules: {
            required: isRequired ? requiredMessage : false
        }
    });

    const handleClose = useCallback(
        (selectedValues?: Item[]) => {
            if (controlledIsOpen === undefined) {
                setInternalIsOpen(false);
            }
            onPanelClose?.(selectedValues);
        },
        [controlledIsOpen, onPanelClose]
    );

    const handleToggle = () => {
        if (isPanelOpen) {
            handleClose(value);
        } else {
            if (controlledIsOpen === undefined) {
                setInternalIsOpen(true);
            }
            onPanelOpen?.();
        }
    };

    const handleClickOutside = useCallback(
        (event: Event) => {
            const target = event.target as HTMLElement;

            if (!containerRef.current?.contains(target) && isPanelOpen) {
                handleClose(value);
            }
        },
        [isPanelOpen, handleClose, value]
    );

    useEvent({ event: 'mouseup', handler: !ref ? handleClickOutside : () => {} });

    const filteredValue =
        value && value.every((item: Item) => item !== null) ? (value as Item[]) : [];

    return {
        containerRef,
        handleClose,
        handleToggle,
        isPanelOpen,
        onChange,
        value: filteredValue
    };
};

export default useMultiSelectField;
